import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import ProbabilityOutcomeTable from "../../../../components/ui/ProbabilityOutcomeTable";

// --- utils ---
function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) { const t = a % b; a = b; b = t; } return a || 1; }
function simplify(p, q) { if (q < 0) { p = -p; q = -q; } const g = gcd(p, q); return [p / g, q / g]; }
function dfrac(p, q) { const [pp, qq] = simplify(p, q); return qq === 1 ? `${pp}` : `\\dfrac{${pp}}{${qq}}`; }
function parseFraction(raw) {
  const s = String(raw).replace(",", ".").trim();
  if (/^[+-]?\d+$/.test(s)) return [parseInt(s,10), 1];
  const m = s.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);
  if (!m || parseInt(m[2],10) === 0) return null;
  return [parseInt(m[1],10), parseInt(m[2],10)];
}

const MODES = [
  { key: "gt", phrase: "większa od",        rel: ">",   latexRel: ">"    },
  { key: "ge", phrase: "nie mniejsza niż",  rel: "\\ge", latexRel: "\\ge" },
  { key: "lt", phrase: "mniejsza od",       rel: "<",   latexRel: "<"    },
  { key: "le", phrase: "nie większa niż",   rel: "\\le", latexRel: "\\le" },
];

function countFav(s, modeKey) {
  let c = 0;
  for (let i = 1; i <= 6; i++) {
    for (let j = 1; j <= 6; j++) {
      const sum = i + j;
      if (
        (modeKey === "gt" && sum >  s) ||
        (modeKey === "ge" && sum >= s) ||
        (modeKey === "lt" && sum <  s) ||
        (modeKey === "le" && sum <= s)
      ) c++;
    }
  }
  return c;
}

function OutcomesTable({ s, modeKey }) {
  return (
    <ProbabilityOutcomeTable
      rows={[1, 2, 3, 4, 5, 6]}
      columns={[1, 2, 3, 4, 5, 6]}
      getCellValue={(firstDie, secondDie) => firstDie + secondDie}
      isHighlighted={(firstDie, secondDie) => {
        const sum = firstDie + secondDie;
        return (
          (modeKey === "gt" && sum > s) ||
          (modeKey === "ge" && sum >= s) ||
          (modeKey === "lt" && sum < s) ||
          (modeKey === "le" && sum <= s)
        );
      }}
      ariaLabel="Tabela sum oczek z dwóch kostek"
    />
  );
}

function generateOne() {
  const s = [4,5,6,7,8,9,10][Math.floor(Math.random()*7)];
  const mode = MODES[Math.floor(Math.random()*MODES.length)];
  const total = 36;
  const fav = countFav(s, mode.key);
  const [p, q] = simplify(fav, total);
  return { s, fav, total, p, q, modeKey: mode.key, phrase: mode.phrase, latexRel: mode.latexRel }; // czyste
}

export function generateTask() {
  const d = generateOne();
  return {
    type: "TwoDiceSumGreaterTask",
    data: d,
    uid: `2dice_${d.modeKey}_${d.s}_${Date.now()}_${Math.floor(Math.random()*1e9)}`
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", margin: "12px 0" }}>
      Rzucamy dwiema sześciennymi kostkami. Oblicz prawdopodobieństwo, że suma wyrzuconych oczek jest{" "}
      <strong>{data.phrase}</strong>{" "}
      <InlineMath math={String(data.s)} />.
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-3">
      <span><InlineMath math={'P(A) = '} /></span>
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label="P(A)"
        placeholder="np. 5/12"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return { correct: false, message: "Podaj ułamek (np. 5/12) lub liczbę." };
  }
  const parsed = parseFraction(userAnswer.raw);
  if (!parsed) return { correct: false, message: "Nieprawidłowy ułamek." };

  const [gp, gq] = parsed;
  const [cp, cq] = simplify(data.p, data.q);
  const [gpp, gqq] = simplify(gp, gq);
  const correct = (gpp === cp && gqq === cq);
  return { correct, expected: `\\tfrac{${cp}}{${cq}}`, needsSimplification: !correct && (gp/gq === cp/cq) };
}

export function renderAnswer({ data }) {
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <InlineMath math={`P(A) = ${dfrac(data.p, data.q)}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <InlineMath math={formatLatexPreview(userAnswer.raw, 'P(A)')} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const rawNum = data.fav, rawDen = data.total;
  const [simpNum, simpDen] = simplify(rawNum, rawDen);
  const tail = (simpNum === rawNum && simpDen === rawDen) ? "" : `= \\dfrac{${simpNum}}{${simpDen}}`;

  const omegaLine = `\\Omega = \\{(i,j): i,j \\in \\{1,2,3,4,5,6\\}\\},\\; |\\Omega| = 36`;
  const condLine  = `\\text{Warunek: } i+j\\, ${data.latexRel} \\, ${data.s}`;
  const probLine  = `P(\\mathrm{A}) = \\dfrac{|\\mathrm{A}|}{|\\Omega|} = \\dfrac{${rawNum}}{${rawDen}} ${tail}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <div style={{ marginBottom: 8 }}>
        <strong>Krok 1 — przestrzeń zdarzeń</strong>
        <BlockMath math={omegaLine} />
      </div>
      <div style={{ marginBottom: 8 }}>
        <strong>Krok 2 — zdarzenie A</strong>
        <BlockMath math={condLine} />
        <div>
          <em>Tabela sum i zaznaczone komórki spełniające warunek {data.phrase} <InlineMath math={String(data.s)} />:</em>
        </div>
        <OutcomesTable s={data.s} modeKey={data.modeKey} />
        <InlineMath math={`|\\mathrm{A}| = ${data.fav}`} />
      </div>
      <div>
        <strong>Krok 3 — obliczenie prawdopodobieństwa</strong>
        <BlockMath math={probLine} />
      </div>
    </div>
  );
}
